/**
 * „Dokończ sprawdzanie” over the map (views/new-mobile-map/_guided_resume.php):
 * on a plain visit a visitor with a parcel check begun in this browser
 * (guided-check/check-progress.js) sees the most recent one, with its
 * progress, the step to open next and its summary, in place of the start
 * dialog opening by itself. Closing the card keeps it away for
 * REOPEN_AFTER_MS, like the dialog, and moves the focus to „Sprawdź krok po
 * kroku” (on a phone to the menu); Escape inside the card closes it the same
 * way. „Zacznij nowe sprawdzanie” hides it and opens the start dialog (its
 * [data-start-dialog-open] listener). Where the list is a bottom sheet the
 * card stands at the top of the list panel (guided-resume-placement.js).
 * Without readable storage nothing shows. Every text comes from the markup.
 */
import { savedChecks } from '../../guided-check/check-progress';
import { closeGuidedResumeOnEscape, placeGuidedResume } from './guided-resume-placement';
import { isPlainVisit, REOPEN_AFTER_MS, visibleStartOpener } from './start-dialog';

var CLOSED_KEY = 'rcn-guided-resume-closed-at';
var PATH = 'dzialka';
var DATE_FORMAT = { day: 'numeric', month: 'long', year: 'numeric' };

function storageOf(win) {
    try {
        return win.localStorage || null;
    } catch (e) {
        return null;
    }
}

function format(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function(match, key) {
        return values[key] !== undefined ? values[key] : match;
    });
}

function parse(text) {
    try {
        return JSON.parse(text || '{}') || {};
    } catch (e) {
        return {};
    }
}

/** The most recent unfinished check of a parcel whose next step is an address of the path. */
export function resumableCheck(storage, base) {
    if (!storage || !base) return null;
    var checks = savedChecks(storage, PATH).filter(function(check) {
        var next = check.progress.next;
        return !check.progress.finished && check.progress.title !== '' && next !== null && next.url.indexOf(base) === 0;
    });
    return checks.length ? checks[0] : null;
}

/** „21 października 2026”, or '' for an unknown moment. */
function day(timestamp) {
    if (!(timestamp > 0)) return '';
    try {
        return new Intl.DateTimeFormat('pl-PL', DATE_FORMAT).format(new Date(timestamp));
    } catch (e) {
        return '';
    }
}

export function initGuidedResume(options) {
    var win = (options && options.window) || window;
    var doc = (options && options.document) || win.document;
    var root = doc ? doc.querySelector('[data-guided-resume]') : null;
    if (!root) return { shown: false };
    var params = (options && options.params) || new URLSearchParams(win.location.search || '');
    var now = (options && options.now) || Date.now;
    var storage = storageOf(win);
    if (!storage || !isPlainVisit(params)) return { shown: false };
    var closedAt = 0;
    try {
        closedAt = Number(storage.getItem(CLOSED_KEY));
    } catch (e) { /* a blocked storage reads as never closed */ }
    if (closedAt > 0 && now() - closedAt < REOPEN_AFTER_MS) return { shown: false };
    var base = root.getAttribute('data-guided-resume-base') || '';
    var check = resumableCheck(storage, base);
    if (!check) return { shown: false };

    var texts = parse(root.getAttribute('data-guided-resume-texts'));
    var progress = check.progress;
    var done = Object.keys(progress.steps).length;
    var total = progress.total > 0 ? progress.total : done;
    var title = root.querySelector('[data-guided-resume-title]');
    var count = root.querySelector('[data-guided-resume-count]');
    var bar = root.querySelector('[data-guided-resume-bar]');
    var next = root.querySelector('[data-guided-resume-next]');
    var summary = root.querySelector('[data-guided-resume-summary]');
    if (title) title.textContent = format(texts.title, { title: progress.title });
    if (count) {
        var opened = day(progress.updated);
        count.textContent = [format(texts.count, { done: done, total: total }), opened ? format(texts.last, { date: opened }) : '']
            .filter(Boolean).join(' ');
    }
    if (bar) {
        bar.setAttribute('aria-valuemax', String(total));
        bar.setAttribute('aria-valuenow', String(done));
        if (bar.firstElementChild) bar.firstElementChild.style.width = (total > 0 ? Math.round((done / total) * 100) : 0) + '%';
    }
    if (next) {
        next.setAttribute('href', progress.next.url);
        next.textContent = progress.next.towards && progress.next.number
            ? format(texts.next, { number: progress.next.number, step: progress.next.towards })
            : String(texts.nextSummary || '');
    }
    if (summary) {
        // The summary is a second way only while the next step is another page.
        var summaryUrl = progress.summary && progress.summary.indexOf(base) === 0 && progress.summary !== progress.next.url ? progress.summary : '';
        if (summaryUrl) summary.setAttribute('href', summaryUrl);
        summary.hidden = summaryUrl === '';
    }
    placeGuidedResume(win, doc, root);
    root.hidden = false;

    var close = root.querySelector('[data-guided-resume-close]');
    if (close) {
        close.addEventListener('click', function() {
            // The focused button goes with the card: the focus moves where the start dialog leaves it.
            var hadFocus = root.contains(doc.activeElement);
            root.hidden = true;
            if (hadFocus) {
                var opener = visibleStartOpener(doc);
                if (opener) opener.focus();
            }
            try {
                storage.setItem(CLOSED_KEY, String(now()));
            } catch (e) { /* the card stays away for this page only */ }
        });
    }
    closeGuidedResumeOnEscape(root);
    var start = root.querySelector('[data-guided-resume-start]');
    if (start) {
        start.addEventListener('click', function() { root.hidden = true; });
    }

    return { shown: true, check: check };
}
